import type ImageType from "@travily/types/shared/Image";
import { blurhashToDataUri } from "@ui/lib/blurhash";
import type { ReactNode } from "react";

/**
 * `ImageType` widened for nil-aspect serializers (`INTRINSIC_TYPELIZE_SHAPE`):
 * dimensions are null until blob analysis has run.
 */
type ImagePayload = Omit<ImageType, "width" | "height"> & {
  width: number | null;
  height: number | null;
};

export interface ImageProps {
  /** Structured image object from a serializer using `ImageResolvable`. */
  image: ImagePayload | null;
  /**
   * CSS `sizes` hint. Tells the browser how wide the rendered image will be at
   * each viewport so it picks the right `srcset` entry. Default: `"100vw"`.
   * Example: `"(min-width: 768px) 820px, 100vw"`.
   */
  sizes?: string;
  /**
   * LCP optimisation. `true` sets `fetchpriority="high"` and `loading="eager"`.
   * Set on exactly one image per page — the one above the fold we expect to be
   * the Largest Contentful Paint. Default: `false`.
   */
  priority?: boolean;
  /** Classes for the `<img>` element (e.g. `object-cover`). */
  className?: string;
  /** Override `image.alt`. Prefer setting `alt` at the serializer. */
  alt?: string;
  /** Rendered when `image === null`. */
  fallback?: ReactNode;
}

/**
 * Renders a structured Image payload. Emits `<picture>` with a WebP `<source>`
 * and a source-format `<img>` fallback, with `width`/`height` when known (for
 * CLS; omitted while null so the browser sizes intrinsically after load),
 * `loading`/`fetchpriority` (for LCP tuning), and a BlurHash placeholder painted
 * permanently behind the image.
 *
 * The placeholder is a `data:` URI decoded from `image.blurhash` (pure JS, no
 * `<canvas>`), so it paints during SSR on the first byte and costs nothing on
 * the main thread — even for off-screen cards in a grid.
 *
 * The placeholder is fully self-contained: it is painted as the `<picture>`'s
 * own `background-image`, which renders beneath the `<img>` descendant and is
 * clipped to the picture's box — no positioning requirements on the parent.
 */
export function Image({ image, sizes = "100vw", priority = false, className, alt, fallback = null }: ImageProps) {
  if (!image) return <>{fallback}</>;

  const placeholder = image.blurhash ? blurhashToDataUri(image.blurhash) : "";
  // Static images (e.g. avatar presets) reuse one source URL for both srcsets;
  // only advertise a WebP `<source>` when it genuinely differs, so we never
  // serve a PNG under `type="image/webp"`.
  const hasWebpSource = image.webp_srcset !== "" && image.webp_srcset !== image.srcset;

  return (
    /* The blurhash background paints beneath the `<img>` descendant, so the
     * real image covers it as soon as it decodes; before decode, the
     * transparent picture lets the placeholder show through.
     * Full-size + block ensures consumer `<img>` classes that rely on
     * percentage sizing (`size-full`, `h-full`) have a sized parent. */
    <picture
      style={{
        display: "block",
        width: "100%",
        height: "100%",
        ...(placeholder && {
          backgroundImage: `url(${placeholder})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }),
      }}
    >
      {hasWebpSource && <source type="image/webp" srcSet={image.webp_srcset} sizes={sizes} />}
      <img
        src={image.src}
        srcSet={image.srcset}
        sizes={sizes}
        width={image.width ?? undefined}
        height={image.height ?? undefined}
        alt={alt ?? image.alt}
        className={className}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : undefined}
        decoding="async"
      />
    </picture>
  );
}
